import React, { ReactNode, useMemo, useState } from "react";

import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";

import { getFullFarmDetails } from "services";

import { fetchFieldPracticesWithCropNames, formatNumber, useGetFieldPracticePoints, useGetFieldVerifications, useTranslation } from "utils";

import { GET_FIELD_PRACTICES } from "consts";

import { FIELD_VERIFICATION_FILTER, FieldVerification, FieldVerificationFilter, FieldVerificationSort, PRACTICE_SLUG, PracticeSlug } from "models";

import { Col, Flex, Icon, IconButton, LinedCard, Pagination, Row, SkeletonWrapper, Switch, TableFieldPanel, Tag, Tooltip, Typography } from "components";

import { FieldDrawer } from "pages/admin/verifications/components/drawers/FieldDrawer";
import { OrganicFertilizerSourcesPanel } from "pages/admin/verifications/components/misc/OrganicFertilizerSourcesPanel";
import { SearchAndFilter } from "pages/admin/verifications/components/misc/SearchAndFilter";
import { VerificationStatusDisplay } from "pages/admin/verifications/components/misc/VerificationStatusDisplay";
import { useFieldPracticesFilterData } from "pages/admin/verifications/hooks/useFieldPracticesFilterData";
import { filterFields } from "pages/admin/verifications/utils/fieldPracticeFilterUtils";
import { getFieldVerificationStatus, paginateItems, sortFields } from "pages/admin/verifications/utils/fieldVerificationUtils";

interface PracticeListProps {
  farmId: string;
  practiceSlugs: PracticeSlug[];
  onUpdate?: () => void;
  renderExtraColumns?: (field: FieldVerification) => ReactNode;
  renderTimeline: (fieldId: string, field: FieldVerification) => ReactNode;
  renderExpandedContent: (field: FieldVerification) => ReactNode;
  additionalDrawerProps?: object;
}

export const PracticeList = ({
  farmId,
  practiceSlugs,
  onUpdate,
  renderExtraColumns,
  renderTimeline,
  renderExpandedContent,
  additionalDrawerProps = {},
}: PracticeListProps) => {
  const [page, setPage] = useState(1);
  const defaultItemsPerPage = 20;
  const [itemsPerPage, setItemsPerPage] = useState(defaultItemsPerPage);
  const [expandedRows, setExpandedRows] = useState<string[]>([]);
  const [showDrawer, setShowDrawer] = useState(false);
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);
  const [filter, setFilter] = useState<FieldVerificationFilter>();
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<FieldVerificationSort>();
  const [onlySoilPlus3, setOnlySoilPlus3] = useState(false);

  const queryClient = useQueryClient();
  const { t } = useTranslation(["practices"], { useSuspense: false });

  // Fetch all fields once (filtering/sorting/pagination done locally)
  const { data: fieldData, isLoading } = useGetFieldVerifications({
    farmId,
    search: search || undefined,
    filter: filter === FIELD_VERIFICATION_FILTER.allFields ? filter : practiceSlugs,
    excludeRedzoneGeometry: true,
    onlySoilPlus3,
  });

  const { data: farmDetails } = useQuery({
    queryKey: ["admin-farm-details", farmId],
    queryFn: async () => {
      const response = await getFullFarmDetails({ id: farmId });
      return response.data;
    },
    enabled: !!farmId,
  });

  // Fetch field-practice-specific points for credits/payout display
  const { getFieldTotals, isLoading: isLoadingPoints } = useGetFieldPracticePoints({
    farmId,
    practiceSlugs,
  });

  // Fetch crops for all fields if this is the crops practice
  const isCropsPractice = practiceSlugs.includes(PRACTICE_SLUG.CROPS);
  // Fetch fertilizers for all fields if this is a fertilizer practice
  const isFertilizerPractice = practiceSlugs.includes(PRACTICE_SLUG.MINERAL_FERTILIZERS) || practiceSlugs.includes(PRACTICE_SLUG.ORGANIC_FERTILIZERS);
  // Fetch tillage/seeding for all fields if this is a tillage/seeding practice
  const isTillageSeedingPractice = practiceSlugs.includes(PRACTICE_SLUG.TILLAGES) || practiceSlugs.includes(PRACTICE_SLUG.SEEDINGS);
  const fieldIds = useMemo(() => fieldData?.fields?.map((f) => f.id) || [], [fieldData?.fields]);

  // Fetch all farm field IDs (including unenrolled) so OrganicFertilizerSourcesPanel can find
  // self-produced practices on fields that aren't enrolled in the organic fertilizers practice.
  const { data: allFarmFieldsData } = useGetFieldVerifications({ farmId, filter: FIELD_VERIFICATION_FILTER.allFields, excludeRedzoneGeometry: true });

  const unenrolledFertilizerFieldIds = useMemo(() => {
    if (!isFertilizerPractice) return [];
    const enrolledSet = new Set(fieldIds);
    return (allFarmFieldsData?.fields ?? []).filter((f) => !enrolledSet.has(f.id) && !f.disabledBySplit).map((f) => f.id);
  }, [isFertilizerPractice, allFarmFieldsData?.fields, fieldIds]);

  const fetchFieldPractices = (fieldId: string) => fetchFieldPracticesWithCropNames({ fieldId, isAdmin: true, t });

  // Fetch field practices once for all practice types (React Query will cache by queryKey)
  // Reuse existing query key pattern to avoid duplicate fetches
  // Same fetch as useGetFieldPractices, so every query on this key caches the same shape
  const shouldFetchPractices = (isCropsPractice || isFertilizerPractice || isTillageSeedingPractice) && fieldIds.length > 0;
  const fieldPracticesQueries = useQueries({
    queries: fieldIds.map((fieldId) => ({
      queryKey: [GET_FIELD_PRACTICES, fieldId, true], // [GET_FIELD_PRACTICES, fieldId, isAdmin]
      queryFn: () => fetchFieldPractices(fieldId),
      enabled: shouldFetchPractices,
      staleTime: 0,
    })),
  });

  // Queries for unenrolled fields — only needed for selfProducedFertilizersByYear
  const unenrolledFieldPracticesQueries = useQueries({
    queries: unenrolledFertilizerFieldIds.map((fieldId) => ({
      queryKey: [GET_FIELD_PRACTICES, fieldId, true],
      queryFn: () => fetchFieldPractices(fieldId),
      enabled: unenrolledFertilizerFieldIds.length > 0,
      staleTime: 0,
    })),
  });

  // Track field practices data for dependency tracking (shared across all practice types)
  const fieldPracticesData = useMemo(
    () =>
      fieldPracticesQueries.map((query, index) => ({
        fieldId: fieldIds[index],
        data: query.data,
        updatedAt: query.dataUpdatedAt,
      })),
    // Note: useQueries returns a non-stable array reference, so we track dataUpdatedAt changes
    // via a string representation. This ensures the memo recalculates when query data updates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fieldPracticesQueries.map((q) => q.dataUpdatedAt).join(","), fieldIds.join(",")]
  );

  const unenrolledFieldPracticesData = useMemo(
    () =>
      unenrolledFieldPracticesQueries.map((query, index) => ({
        fieldId: unenrolledFertilizerFieldIds[index],
        data: query.data,
        updatedAt: query.dataUpdatedAt,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [unenrolledFieldPracticesQueries.map((q) => q.dataUpdatedAt).join(","), unenrolledFertilizerFieldIds.join(",")]
  );

  // Extract all filtering data using custom hook
  const {
    fieldCropsMap,
    fieldFertilizersMap,
    fieldTillagesMap,
    fieldSeedingsMap,
    uniqueCrops,
    availableCropTypes,
    availableYears,
    uniqueFertilizers,
    availableFertilizerTypes,
    availableFertilizerYears,
    uniqueTillages,
    uniqueSeedings,
    availableTillageYears,
    availableSeedingYears,
  } = useFieldPracticesFilterData({
    fieldPracticesData,
    isCropsPractice,
    isFertilizerPractice,
    isTillageSeedingPractice,
  });

  // Derive self-produced organic fertilizer IDs per year, for use by OrganicFertilizerSourcesPanel.
  // Uses both enrolled and unenrolled field practices — a field may have self-produced fertilizers
  // without being enrolled in the organic fertilizers practice.
  const selfProducedFertilizersByYear = useMemo<Record<number, string[]>>(() => {
    if (!isFertilizerPractice) return {};
    const map: Record<number, string[]> = {};
    [...fieldPracticesData, ...unenrolledFieldPracticesData].forEach(({ data }) => {
      data?.practices
        .filter((practice) => practice.type === PRACTICE_SLUG.ORGANIC_FERTILIZERS && practice.selfProduced === true)
        .forEach((practice) => {
          if (practice.year == null || !Number.isFinite(practice.year)) return;
          if (!map[practice.year]) map[practice.year] = [];
          if (!map[practice.year].includes(practice.assignedPracticeId)) map[practice.year].push(practice.assignedPracticeId);
        });
    });
    return map;
  }, [isFertilizerPractice, fieldPracticesData, unenrolledFieldPracticesData]);

  // Apply local filtering and sorting
  const processedFields = useMemo(() => {
    if (!fieldData?.fields) return [];
    const filtered = filterFields(fieldData.fields, filter, fieldCropsMap, fieldFertilizersMap, fieldTillagesMap, fieldSeedingsMap);
    const sorted = sortFields(filtered, sort);
    return sorted;
  }, [fieldData?.fields, filter, sort, fieldCropsMap, fieldFertilizersMap, fieldTillagesMap, fieldSeedingsMap]);

  // Apply local pagination
  const paginatedFields = useMemo(() => paginateItems(processedFields, page, itemsPerPage), [processedFields, page, itemsPerPage]);

  // Easter egg: Karol's Credit Generator™ - 1/5 chance for credits to show in blue with special tooltip
  const karolCreditEasterEggMap = useMemo(() => {
    const map = new Set<string>();
    paginatedFields.forEach((field) => {
      if (Math.random() < 1 / 30) {
        map.add(field.id);
      }
    });
    return map;
  }, [paginatedFields]);

  const handlePageChange = (newPage: number, pageSize: number) => {
    setPage(newPage);
    setItemsPerPage(pageSize);
  };

  const handleFilterChange = (newFilter: string) => {
    setFilter(newFilter as FieldVerificationFilter);
    setPage(1); // Reset to first page when filter changes
  };

  const handleSortChange = (newSort: FieldVerificationSort | undefined) => {
    setSort(newSort);
    setPage(1); // Reset to first page when sort changes
  };

  const toggleRowExpansion = (fieldId: string) => {
    setExpandedRows((prev) => (prev.includes(fieldId) ? prev.filter((id) => id !== fieldId) : [...prev, fieldId]));
  };

  const handleDrawerOpen = (fieldId: string) => {
    setSelectedFieldId(fieldId);
    setShowDrawer(true);
  };

  const handleDrawerClose = () => {
    setShowDrawer(false);
    setTimeout(() => setSelectedFieldId(null), 200);
  };

  const handleDataChange = () => {
    queryClient.invalidateQueries({ queryKey: ["admin-field-verifications", farmId] });
    // Also invalidate field practices queries to ensure fresh data
    queryClient.invalidateQueries({ queryKey: [GET_FIELD_PRACTICES] });
  };

  return (
    <>
      <SearchAndFilter
        setFilter={handleFilterChange}
        setPage={setPage}
        setSearch={setSearch}
        setSort={handleSortChange}
        type="practice"
        practiceSlug={practiceSlugs}
        uniqueCrops={uniqueCrops}
        availableCropTypes={availableCropTypes}
        availableYears={availableYears}
        uniqueFertilizers={uniqueFertilizers}
        availableFertilizerTypes={availableFertilizerTypes}
        availableFertilizerYears={availableFertilizerYears}
        uniqueTillages={uniqueTillages}
        uniqueSeedings={uniqueSeedings}
        availableTillageYears={availableTillageYears}
        availableSeedingYears={availableSeedingYears}
      />
      <Flex vertical gap="0.5rem">
        <Flex gap={16} align="center">
          Only Soil Plus 3.0
          <Switch
            value={onlySoilPlus3}
            onChange={(v) => {
              setPage(1);
              setOnlySoilPlus3(v);
            }}
          />
        </Flex>
        {isFertilizerPractice && <OrganicFertilizerSourcesPanel farmId={farmId} selfProducedFertilizersByYear={selfProducedFertilizersByYear} />}
        <Row>
          <Col>
            {!isLoading && processedFields.length === 0 && <Typography variant="paragraph-m">There are no fields to verify.</Typography>}

            <div className="verification-crop-table">
              {/* Table Header */}
              <Row align="middle" className="table-header">
                <Col md={6}>
                  <Typography variant="paragraph-xs" bold>
                    Field Name
                  </Typography>
                </Col>
                <Col md={2}>
                  <Typography variant="paragraph-xs" bold>
                    Size
                  </Typography>
                </Col>
                <Col md={2}>
                  <Typography variant="paragraph-xs" bold>
                    Redzone
                  </Typography>
                </Col>
                {renderExtraColumns && (
                  <Col md={2}>
                    <Typography variant="paragraph-xs" bold>
                      Editing Request
                    </Typography>
                  </Col>
                )}
                <Col md={2}>
                  <Typography variant="paragraph-xs" bold>
                    Start Year
                  </Typography>
                </Col>
                <Col md={3}>
                  <Typography variant="paragraph-xs" bold>
                    Credit Generation
                  </Typography>
                </Col>
                <Col md={3}>
                  <Typography variant="paragraph-xs" bold>
                    Verification Status
                  </Typography>
                </Col>
                <Col md={2}></Col>
              </Row>

              {paginatedFields.map((field) => {
                const isExpanded = expandedRows.includes(field.id);
                const fieldTotals = getFieldTotals(field.id);
                const hasPointsChange = fieldTotals && Math.abs(fieldTotals.credits - fieldTotals.creditsInitial) > 0.01;
                const relevantParticipations = field.practiceParticipations.filter(({ practiceSlug }) => practiceSlugs.includes(practiceSlug));
                const isDiscontinued = relevantParticipations.every(({ discontinuedYear }) => !!discontinuedYear);
                const discontinuedYear = relevantParticipations.find(({ discontinuedYear }) => !!discontinuedYear)?.discontinuedYear;
                const startYear = relevantParticipations.sort((a, b) => a.startYear - b.startYear)[0]?.startYear ?? "unknown";

                let soilPlus3TagText = null;

                if (field.hasSoilPlus3Data) {
                  if (relevantParticipations.some((p) => p.wasNormalized)) {
                    soilPlus3TagText = "soil+ 3.0 normalized";
                  } else if (relevantParticipations.some((p) => p.isNew)) {
                    soilPlus3TagText = "soil+ 3.0 new";
                  } else {
                    soilPlus3TagText = "soil+ 3.0 data added";
                  }
                }

                return (
                  <LinedCard
                    className="margin-bottom-100"
                    key={field.id}
                    primary={
                      <Row align="middle">
                        <Col md={6}>
                          {/* @ts-expect-error not in ts yet */}
                          <TableFieldPanel
                            field={field}
                            tag={soilPlus3TagText && <Tag text={soilPlus3TagText} colour="yellow" />}
                            badges={
                              field.commentCount > 0 && (
                                <>
                                  <Icon iconName={"tabler/message-2"} className="icon inline" size="s" />
                                  {field.commentCount}
                                </>
                              )
                            }
                            isAdmin
                          />
                        </Col>
                        <Col md={2}>
                          <Typography variant="paragraph-xs">{formatNumber({ number: field.size, decimalPlaces: 4 })}ha</Typography>
                        </Col>
                        <Col md={2}>
                          {field.redzone && (
                            <Tag colour={field.redzone.confirmed ? "blue" : "red"} text={field.redzone.confirmed ? "confirmed redzone" : "redzone warning"} />
                          )}
                        </Col>
                        {renderExtraColumns && <Col md={2}>{renderExtraColumns(field)}</Col>}
                        <Col md={2}>
                          <Typography variant="paragraph-xs">{startYear}</Typography>
                          {isDiscontinued && (
                            <Typography variant="paragraph-xs" color="red-500">
                              {" "}
                              - Discontinued {discontinuedYear}
                            </Typography>
                          )}
                        </Col>
                        <Col md={3}>
                          <SkeletonWrapper isLoading={isLoadingPoints} type="button" buttonShape="round" buttonSize="small" className="margin-0">
                            {fieldTotals ? (
                              <Tooltip
                                title={
                                  karolCreditEasterEggMap.has(field.id)
                                    ? "📜 You found an Elder Scroll: Karol's Credit Prophecy™. The ancient text reads: '1 Ton of CO₂ = 1 credit.' The Dragonborn is underwhelmed."
                                    : ""
                                }
                              >
                                <div>
                                  <span className={`paragraph-xs-bold ${karolCreditEasterEggMap.has(field.id) ? "green-text" : ""}`}>
                                    {formatNumber({ number: fieldTotals.credits, decimalPlaces: 2 })}
                                  </span>
                                  {hasPointsChange && (
                                    <span className="paragraph-xs grey-text"> / {formatNumber({ number: fieldTotals.creditsInitial, decimalPlaces: 2 })}</span>
                                  )}
                                </div>
                              </Tooltip>
                            ) : (
                              <span className="paragraph-xs grey-text">-</span>
                            )}
                          </SkeletonWrapper>
                        </Col>
                        <Col md={3}>
                          <SkeletonWrapper isLoading={isLoading} type="button" buttonShape="round" buttonSize="small" className="margin-0">
                            <Status field={field} />
                          </SkeletonWrapper>
                        </Col>
                        <Col md={2}>
                          <div className="flex-center-align gap-25">
                            <IconButton
                              iconName="klim/field"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDrawerOpen(field.id);
                              }}
                            />
                            <IconButton iconName={isExpanded ? "tabler/chevron-up" : "tabler/chevron-down"} onClick={() => toggleRowExpansion(field.id)} />
                          </div>
                        </Col>
                      </Row>
                    }
                    secondary={renderTimeline(field.id, field)}
                    tertiary={isExpanded && renderExpandedContent(field)}
                  />
                );
              })}
            </div>

            {/* Pagination */}
            {processedFields.length > 0 && (
              <Pagination
                onChange={handlePageChange}
                total={processedFields.length}
                current={page}
                pageSizeOptions={[10, 20, 50, 100]}
                defaultPageSize={defaultItemsPerPage}
                pageSize={itemsPerPage}
                hideOnSinglePage={true}
                showSizeChanger={true}
              />
            )}
          </Col>
        </Row>
      </Flex>
      {/* Field Drawer */}
      {selectedFieldId && fieldData?.fields && (
        <FieldDrawer
          field={fieldData.fields.find((field) => field.id === selectedFieldId)!}
          farm={{
            addressStreet: farmDetails?.addressStreet ?? "",
            addressNumber: farmDetails?.addressNumber ?? "",
            addressCity: farmDetails?.addressCity ?? "",
            zip: farmDetails?.zip ?? "",
            region: farmDetails?.region ?? "",
            country: farmDetails?.country ?? "",
          }}
          farmId={farmId}
          visible={showDrawer}
          loadingFields={isLoading}
          onClose={handleDrawerClose}
          onDataChange={() => {
            handleDataChange();
            onUpdate?.();
          }}
          payoutApproved={false}
          {...additionalDrawerProps}
        />
      )}
    </>
  );
};

const Status = ({ field }: { field: FieldVerification }) => (
  <div className="flex-center-align">
    <VerificationStatusDisplay status={getFieldVerificationStatus(field?.verifications)} />
  </div>
);
